import React, {useMemo} from 'react';
import {getCartRecurringCadence, type CartItem, type CartLine} from '../types';
import {lineCharge, memberSavingsPct} from '../context/CartContext';
import PromoCodeField from './PromoCodeField';
import GiftCardField from './GiftCardField';
import MembershipNudge from './MembershipNudge';
import {ShoppingCart, Sparkles, Star} from 'lucide-react';

function formatDollars(cents: number): string {
    return (cents / 100).toFixed(2);
}

function ThumbIcon({line}: {line: CartLine}) {
    if (line.image_url) {
        return <img src={line.image_url} alt="" className="w-full h-full object-contain p-1" />;
    }
    if (line.kind === 'custom_kit') return <Sparkles className="h-4 w-4 text-mld-primary" />;
    if (line.kind === 'curated_rec') return <Star className="h-4 w-4 text-mld-secondary" />;
    return <ShoppingCart className="h-4 w-4 text-mld-muted-text/60" />;
}

interface OrderSummaryProps {
    items: CartItem[];
    subtotalCents: number;
    deliveryCents: number;
    discountCents: number;
    giftCardCents: number;
    taxCents: number;
    totalCents: number;
    promoCode: string | null;
    giftCardCode: string | null;
    onApplyPromo: (code: string, discountCents: number) => void;
    onClearPromo: () => void;
    onApplyGiftCard: (code: string, balanceCents: number) => void;
    onClearGiftCard: () => void;
    showPromoFields?: boolean;
    /** Subtotal eligible for promotions; memberships are intentionally excluded. */
    promoSubtotalCents?: number;
    hasRecurring?: boolean;
    employerCoversCents?: number;
    /** True when the patient has an active My Labs Plus membership. */
    isMember?: boolean;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
    items,
    subtotalCents,
    deliveryCents,
    discountCents,
    giftCardCents,
    taxCents,
    totalCents,
    promoCode,
    giftCardCode,
    onApplyPromo,
    onClearPromo,
    onApplyGiftCard,
    onClearGiftCard,
    showPromoFields = true,
    promoSubtotalCents = subtotalCents,
    hasRecurring = false,
    employerCoversCents,
    isMember = false,
}) => {
    const savingsPct = useMemo(() => memberSavingsPct(items), [items]);
    const recurringCadence = useMemo(() => getCartRecurringCadence(items), [items]);
    return (
        <div className="bg-mld-menu-background rounded-xl border border-mld-panel-border p-6 sticky top-8">
            <h2 className="text-lg font-semibold text-mld-text-primary mb-1">Order summary</h2>
            <p className="text-xs text-mld-muted-text mb-4">{items.reduce((s, i) => s + i.quantity, 0)} items</p>

            {/* Line list */}
            <div className="space-y-3 mb-4">
                {items.map(({line, quantity}) => {
                    const charge = lineCharge(line, isMember);
                    const showsMemberPrice = isMember && line.member_price_cents != null;
                    return (
                        <div key={line.key} className="flex gap-3">
                            <div className="w-12 h-12 rounded bg-mld-menu-background flex-shrink-0 overflow-hidden flex items-center justify-center">
                                <ThumbIcon line={line} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-mld-text-primary font-medium truncate">{line.name}</p>
                                <p className="text-xs text-mld-muted-text">
                                    {line.kind === 'product' ? `Qty: ${quantity}` : (
                                        line.kind === 'custom_kit' && line.mode === 'member'
                                            ? 'Membership'
                                            : line.kind === 'custom_kit'
                                                ? 'One-time kit'
                                                : line.kind === 'membership'
                                                    ? 'My Labs+ membership'
                                                : 'Program'
                                    )}
                                </p>
                            </div>
                            <p className="text-sm font-medium text-mld-text-primary">
                                ${formatDollars(charge * quantity)}
                                {line.recurring && (
                                    <span className="text-[10px] text-mld-muted-text">
                                        {line.recurring === 'monthly' ? ' /mo' : ' /yr'}
                                    </span>
                                )}
                                {showsMemberPrice && (
                                    <span className="block text-[10px] font-medium text-mld-primary text-right">
                                        Member price
                                    </span>
                                )}
                            </p>
                        </div>
                    );
                })}
            </div>

            {!isMember && !items.some(({line}) => line.kind === 'membership') && (
                <div className="mb-4">
                    <MembershipNudge savingsPct={savingsPct} />
                </div>
            )}

            {/* Promo + Gift card fields */}
            {showPromoFields && promoSubtotalCents > 0 && (
                <div className="space-y-3 border-t border-mld-panel-border pt-4 mb-4">
                    <PromoCodeField
                        subtotalCents={promoSubtotalCents}
                        onApply={onApplyPromo}
                        onClear={onClearPromo}
                        appliedCode={promoCode}
                        appliedDiscount={discountCents}
                    />
                    <GiftCardField
                        onApply={onApplyGiftCard}
                        onClear={onClearGiftCard}
                        appliedCode={giftCardCode}
                        appliedAmount={giftCardCents}
                    />
                </div>
            )}

            {/* Totals */}
            <div className="border-t border-mld-panel-border pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                    <span className="text-mld-muted-text">Subtotal</span>
                    <span className="text-mld-text-primary">${formatDollars(subtotalCents)}</span>
                </div>
                {discountCents > 0 && (
                    <div className="flex justify-between text-sm">
                        <span className="text-mld-success">Discount</span>
                        <span className="text-mld-success">-${formatDollars(discountCents)}</span>
                    </div>
                )}
                <div className="flex justify-between text-sm">
                    <span className="text-mld-muted-text">Delivery</span>
                    <span className="text-mld-text-primary">{deliveryCents > 0 ? `$${formatDollars(deliveryCents)}` : 'Free'}</span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-mld-muted-text">Tax</span>
                    <span className="text-mld-muted-text">{taxCents > 0 ? `$${formatDollars(taxCents)}` : 'Calculated at next step'}</span>
                </div>
                {giftCardCents > 0 && (
                    <div className="flex justify-between text-sm">
                        <span className="text-mld-success">Gift card</span>
                        <span className="text-mld-success">-${formatDollars(giftCardCents)}</span>
                    </div>
                )}
                {employerCoversCents != null && employerCoversCents > 0 && (
                    <div className="flex justify-between text-sm">
                        <span className="text-mld-success">Employer covers</span>
                        <span className="text-mld-success">-${formatDollars(employerCoversCents)}</span>
                    </div>
                )}
            </div>

            <hr className="my-4 border-mld-panel-border" />

            <div className="flex justify-between text-lg font-bold text-mld-text-primary">
                <span>{employerCoversCents ? 'You pay' : 'Total amount'}</span>
                <span>${formatDollars(totalCents - (employerCoversCents ?? 0))}</span>
            </div>
            {hasRecurring && recurringCadence && (
                <p className="mt-3 text-[11px] text-mld-muted-text leading-relaxed">
                    {recurringCadence === 'mixed'
                        ? 'Includes recurring subscriptions. You’ll be charged today and again according to each item’s billing schedule.'
                        : `Includes ${recurringCadence === 'annual' ? 'an annual' : 'a monthly'} subscription. You’ll be charged today and on the same date each ${
                            recurringCadence === 'annual' ? 'year' : 'month'
                        } thereafter.`}{' '}
                    Cancel anytime from your dashboard.
                </p>
            )}
        </div>
    );
};

export default OrderSummary;
